import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CheckCircle2, Circle, Trash2, Mail, Users, Search, Filter, Plus, MailCheck } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const PRIO_STYLES = {
  low: "border-zinc-500/30 text-zinc-400",
  normal: "border-white/15 text-zinc-300",
  high: "border-red-500/40 text-red-300",
};

function todayYMD() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function CastNotes({ embedded = false }) {
  const { user } = useAuth();
  const isSender = user.role === "org_admin" || user.role === "production_staff";

  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [rehearsals, setRehearsals] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [filterRecipient, setFilterRecipient] = useState("all");
  const [filterUnread, setFilterUnread] = useState(false);
  const [query, setQuery] = useState("");

  const emptyForm = { content: "", recipient_user_ids: [], rehearsal_id: "", scene_id: "", priority: "normal", also_email: true };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // new-scene dialog
  const [openNewScene, setOpenNewScene] = useState(false);
  const [newScene, setNewScene] = useState({ name: "", act: "1", is_song: false });

  const load = async () => {
    const [n, u, r, s] = await Promise.all([
      api.get("/cast-notes"),
      api.get("/users"),
      api.get("/rehearsals"),
      api.get("/scenes"),
    ]);
    setNotes(n.data);
    setUsers(u.data);
    setRehearsals(r.data);
    setScenes(s.data);
    // default rehearsal to today's, only if form has none picked yet
    setForm((f) => {
      if (f.rehearsal_id) return f;
      const today = todayYMD();
      const t = r.data.find((x) => x.date === today);
      return t ? { ...f, rehearsal_id: t.id } : f;
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const castUsers = useMemo(() => users.filter((u) => u.role === "cast"), [users]);

  const toggleRecipient = (uid) => {
    setForm((f) => {
      const has = f.recipient_user_ids.includes(uid);
      return { ...f, recipient_user_ids: has ? f.recipient_user_ids.filter((x) => x !== uid) : [...f.recipient_user_ids, uid] };
    });
  };
  const selectAllCast = () => setForm((f) => ({ ...f, recipient_user_ids: castUsers.map((u) => u.id) }));
  const clearRecipients = () => setForm((f) => ({ ...f, recipient_user_ids: [] }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.content.trim()) return toast.error("Type a note first");
    if (form.recipient_user_ids.length === 0) return toast.error("Choose at least one recipient");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        rehearsal_id: form.rehearsal_id || null,
        scene_id: form.scene_id || null,
      };
      const { data } = await api.post("/cast-notes", payload);
      toast.success(`Saved ${data.count} note${data.count === 1 ? "" : "s"}${form.also_email ? " · queued for email" : ""}`);
      // keep recipients + rehearsal for rapid note entry, clear the content only
      setForm((f) => ({ ...f, content: "" }));
      load();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const finalize = async () => {
    if (finalizing) return;
    const scoped = form.rehearsal_id;
    const label = scoped ? "for this rehearsal" : "for the whole production";
    if (!confirm(`Send queued email digests ${label}?`)) return;
    setFinalizing(true);
    try {
      const params = scoped ? { rehearsal_id: scoped } : {};
      const { data } = await api.post("/cast-notes/finalize", null, { params });
      if (data.notes_sent === 0) {
        toast.info("No notes queued for email.");
      } else {
        toast.success(`Emailed ${data.notes_sent} note${data.notes_sent === 1 ? "" : "s"} to ${data.recipients} recipient${data.recipients === 1 ? "" : "s"} (mock delivery)`);
      }
      load();
    } catch {
      toast.error("Failed to send emails");
    } finally {
      setFinalizing(false);
    }
  };

  const toggleRead = async (n, read) => {
    await api.patch(`/cast-notes/${n.id}`, { read });
    load();
  };
  const del = async (id) => {
    if (!confirm("Delete this cast note?")) return;
    await api.delete(`/cast-notes/${id}`);
    load();
  };

  const createScene = async () => {
    if (!newScene.name.trim()) return toast.error("Scene name required");
    try {
      const { data } = await api.post("/scenes", newScene);
      toast.success("Scene added");
      setOpenNewScene(false);
      setNewScene({ name: "", act: "1", is_song: false });
      // Commit the new scene into state synchronously so Radix Select registers
      // the SelectItem BEFORE we set the controlled value below.
      flushSync(() => setScenes((prev) => [...prev, data]));
      setForm((f) => ({ ...f, scene_id: data.id }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add scene");
    }
  };

  const shown = notes.filter((n) => {
    if (filterRecipient !== "all" && n.recipient_user_id !== filterRecipient) return false;
    if (filterUnread && n.read_at) return false;
    if (query && !n.content.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const unreadCount = notes.filter((n) => !n.read_at && n.recipient_user_id === user.id).length;
  const pendingEmail = notes.filter((n) => n.queue_email && !n.emailed_at &&
    (!form.rehearsal_id || n.rehearsal_id === form.rehearsal_id));
  const pendingByRecipient = new Set(pendingEmail.map((n) => n.recipient_user_id)).size;

  const todaysRehearsal = rehearsals.find((r) => r.date === todayYMD());

  return (
    <div className={embedded ? "space-y-4" : "space-y-6 animate-fade-up"} data-testid="cast-notes-page">
      {!embedded && (
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Delivery</div>
            <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Cast Notes</h1>
            <p className="mt-1 text-zinc-400 text-sm">
              {isSender
                ? "Take notes during rehearsal. Recipients see them in-app immediately. When you're done, click Finalize & Send to email the digest."
                : `Notes addressed to you.${unreadCount > 0 ? ` ${unreadCount} unread.` : ""}`}
            </p>
          </div>

          {isSender && (
            <button
              data-testid="cn-finalize-btn"
              onClick={finalize}
              disabled={finalizing || pendingEmail.length === 0}
              style={pendingEmail.length ? goldBtn : undefined}
              className={`h-10 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 transition-all
                ${pendingEmail.length
                  ? "hover:brightness-110 shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_10px_24px_-8px_rgba(245,158,11,0.5)]"
                  : "border border-white/10 text-zinc-500 cursor-not-allowed"}`}
            >
              <MailCheck className="w-4 h-4" />
              {pendingEmail.length
                ? `Finalize & Send · ${pendingEmail.length} note${pendingEmail.length === 1 ? "" : "s"} → ${pendingByRecipient} recipient${pendingByRecipient === 1 ? "" : "s"}`
                : "No emails queued"}
            </button>
          )}
        </header>
      )}
      {embedded && isSender && (
        <div className="flex justify-end">
          <button
            data-testid="cn-finalize-btn"
            onClick={finalize}
            disabled={finalizing || pendingEmail.length === 0}
            style={pendingEmail.length ? goldBtn : undefined}
            className={`h-9 px-3 rounded text-sm font-semibold inline-flex items-center gap-1.5 transition-all
              ${pendingEmail.length ? "hover:brightness-110" : "border border-white/10 text-zinc-500 cursor-not-allowed"}`}
          >
            <MailCheck className="w-4 h-4" />
            {pendingEmail.length
              ? `Finalize · ${pendingEmail.length} pending`
              : "No emails queued"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {isSender && (
          <form onSubmit={submit} className="lg:col-span-2 rounded-lg border border-gold/30 bg-gradient-to-br from-[#141210] to-[#121212] p-5 space-y-4" data-testid="cast-notes-composer">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Quick note</div>
              {todaysRehearsal && form.rehearsal_id === todaysRehearsal.id && (
                <span data-testid="cn-today-hint" className="text-[10px] uppercase tracking-widest text-emerald-400">Tonight: {todaysRehearsal.title}</span>
              )}
            </div>

            <div>
              <Textarea
                autoFocus
                data-testid="cn-content-input"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Type the note… e.g. 'Louder projection on entrance, take a beat before the line.'"
                rows={4}
                className="bg-[#0A0A0A] border-white/10 focus:border-gold focus:ring-1 focus:ring-gold"
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e); }}
              />
              <div className="mt-1 text-[10px] text-zinc-500">Tip: press ⌘/Ctrl + Enter to save.</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Recipients</Label>
                <div className="flex items-center gap-2 text-[11px]">
                  <button type="button" onClick={selectAllCast} className="text-gold hover:underline">All cast</button>
                  <span className="text-zinc-600">·</span>
                  <button type="button" onClick={clearRecipients} className="text-zinc-400 hover:text-white">Clear</button>
                </div>
              </div>
              <div data-testid="cn-recipients" className="flex flex-wrap gap-1.5 max-h-40 overflow-auto rounded border border-white/10 bg-[#0A0A0A] p-2">
                {castUsers.length === 0 && <div className="text-xs text-zinc-500 p-2">No cast members in this production yet.</div>}
                {castUsers.map((u) => {
                  const active = form.recipient_user_ids.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      data-testid={`cn-recipient-${u.id}`}
                      onClick={() => toggleRecipient(u.id)}
                      className={`text-xs rounded-full px-2.5 py-1 border transition-all
                        ${active ? "border-gold text-black bg-gold shadow-[0_0_0_1px_rgba(245,158,11,0.5)]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}
                    >
                      {u.name}
                      {u.character && <span className={`ml-1 ${active ? "text-black/70" : "text-zinc-500"}`}>· {u.character}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Rehearsal</Label>
                <Select value={form.rehearsal_id || "none"} onValueChange={(v) => setForm({ ...form, rehearsal_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cn-rehearsal-select" className="bg-[#0A0A0A] border-white/10 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white max-h-80">
                    <SelectItem value="none">— none —</SelectItem>
                    {rehearsals.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{fmtDate(r.date)} · {r.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">Scene</Label>
                  <button
                    type="button"
                    data-testid="cn-add-scene-btn"
                    onClick={() => setOpenNewScene(true)}
                    className="text-[10px] uppercase tracking-widest text-gold hover:underline inline-flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                <Select value={form.scene_id || "none"} onValueChange={(v) => setForm({ ...form, scene_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cn-scene-select" className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white max-h-80">
                    <SelectItem value="none">— none —</SelectItem>
                    {scenes.map((s) => <SelectItem key={s.id} value={s.id}>Act {s.act} · {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="cn-priority-select" className="bg-[#0A0A0A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end justify-between gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-1"><Mail className="w-3 h-3" />Queue for email</Label>
                  <div className="text-[10px] text-amber-400 uppercase tracking-widest mt-1">Sent on finalize</div>
                </div>
                <Switch data-testid="cn-email-toggle" checked={form.also_email} onCheckedChange={(v) => setForm({ ...form, also_email: v })} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-500 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {form.recipient_user_ids.length} selected
              </div>
              <button
                type="submit"
                data-testid="cn-send-btn"
                disabled={submitting}
                style={goldBtn}
                className="h-10 px-4 rounded-md font-semibold text-sm hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5 shadow-[0_0_0_1px_rgba(245,158,11,0.4),0_10px_24px_-8px_rgba(245,158,11,0.5)]"
              >
                <Send className="w-4 h-4" /> Save note
              </button>
            </div>
          </form>
        )}

        {/* Notes list */}
        <div className={`${isSender ? "lg:col-span-3" : "lg:col-span-5"} space-y-3`}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="cn-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search notes" className="bg-[#121212] border-white/10 pl-9" />
            </div>
            {isSender && (
              <Select value={filterRecipient} onValueChange={setFilterRecipient}>
                <SelectTrigger className="bg-[#121212] border-white/10 w-48"><Filter className="w-3.5 h-3.5 mr-1 text-zinc-500" /><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-80">
                  <SelectItem value="all">All recipients</SelectItem>
                  {castUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <label className="text-xs text-zinc-400 flex items-center gap-2 pl-1">
              <input type="checkbox" data-testid="cn-filter-unread" checked={filterUnread} onChange={(e) => setFilterUnread(e.target.checked)} className="accent-gold" />
              Unread only
            </label>
          </div>

          {shown.length === 0 && (
            <div className="rounded-md border border-dashed border-white/10 p-8 text-center text-sm text-zinc-500">
              {notes.length === 0 ? (isSender ? "No cast notes yet. Type one on the left to get started." : "No cast notes for you yet.") : "No notes match those filters."}
            </div>
          )}

          {shown.map((n) => {
            const mine = n.recipient_user_id === user.id;
            const read = !!n.read_at;
            const queued = n.queue_email && !n.emailed_at;
            const emailed = !!n.emailed_at;
            return (
              <div key={n.id} data-testid={`cn-item-${n.id}`} className={`rounded-lg border ${read ? "border-white/10" : "border-gold/40"} bg-[#121212] p-4`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggleRead(n, !read)}
                    data-testid={`cn-toggle-read-${n.id}`}
                    title={read ? "Mark unread" : "Mark read"}
                    className={`mt-1 shrink-0 ${read ? "text-emerald-400" : "text-gold"} hover:brightness-125`}
                  >
                    {read ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                      {isSender && (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-zinc-300">
                          For {n.recipient_name || "unknown"}{n.recipient_character ? ` · ${n.recipient_character}` : ""}
                        </span>
                      )}
                      {mine && !isSender && (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-zinc-300">To you</span>
                      )}
                      {n.rehearsal_title && (
                        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-zinc-400">
                          {fmtDate(n.rehearsal_date)} · {n.rehearsal_title}
                        </span>
                      )}
                      <span className={`rounded-full border px-2 py-0.5 uppercase tracking-widest ${PRIO_STYLES[n.priority] || PRIO_STYLES.normal}`}>{n.priority}</span>
                      {queued && <span className="rounded-full border border-amber-500/30 text-amber-300 px-2 py-0.5 uppercase tracking-widest inline-flex items-center gap-1"><Mail className="w-3 h-3" />queued</span>}
                      {emailed && <span className="rounded-full border border-emerald-500/30 text-emerald-300 px-2 py-0.5 uppercase tracking-widest inline-flex items-center gap-1"><MailCheck className="w-3 h-3" />emailed</span>}
                    </div>
                    <div className="mt-2 text-sm text-white whitespace-pre-wrap">{n.content}</div>
                    <div className="mt-2 text-[11px] text-zinc-500">
                      From {n.created_by_name} · {fmtDateTime(n.created_at)}
                      {read && <span className="ml-2 text-emerald-400">read {fmtDateTime(n.read_at)}</span>}
                      {emailed && <span className="ml-2 text-emerald-400">emailed {fmtDateTime(n.emailed_at)}</span>}
                    </div>
                  </div>
                  {isSender && (
                    <button data-testid={`cn-del-${n.id}`} onClick={() => del(n.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* New scene dialog */}
      <Dialog open={openNewScene} onOpenChange={setOpenNewScene}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Add scene</DialogTitle>
            <DialogDescription className="text-zinc-500">Quickly add a scene so you can tag notes with it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                autoFocus
                data-testid="cn-new-scene-name"
                value={newScene.name}
                onChange={(e) => setNewScene({ ...newScene, name: e.target.value })}
                placeholder="e.g. Act 1 Scene 3, or Agony (reprise)"
                className="bg-[#0A0A0A] border-white/10"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Act</Label>
                <Input value={newScene.act} onChange={(e) => setNewScene({ ...newScene, act: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label>Song?</Label>
                  <div className="pt-2"><Switch checked={newScene.is_song} onCheckedChange={(v) => setNewScene({ ...newScene, is_song: v })} /></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cn-new-scene-save" onClick={createScene} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save scene</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
